// Menedżer podstron CMS

import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FileEarmarkPlus } from 'react-bootstrap-icons';

import { reduxActions, useDispatch } from 'store';
import { useGetCmsArticles, axios } from 'api';
import { Button } from 'components/controls';
import { ICmsArticleItem } from 'api/types';

import PageCard from './components/PageCard/PageCard';
import EditPage from './components/EditPage/EditPage';

import styles from 'theme/pages/PanelPages/PanelPages.module.scss';

const SECTION_ID = 62;
const URL_FIELD_SYMBOL = 'url';

const PanelPages = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [editingId, setEditingId] = useState<number | null | 'new'>(null);

  // URL-e podstron zaciągane osobno (żeby pokazać w karcie bez pełnego fetch)
  const [urlMap, setUrlMap] = useState<Record<number, string>>({});

  const { data, refetch, isLoading } = useGetCmsArticles({
    page: 1,
    limit: 100,
    section_id: SECTION_ID
  } as any);

  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Panel'), path: 'managment_panel' },
        { name: t('Podstrony'), path: undefined }
      ])
    );
  }, []);

  // Po załadowaniu listy - pobierz URL dla każdej podstrony
  useEffect(() => {
    if (!data?.items?.length) return;

    Promise.all(
      data.items.map((item) =>
        axios
          .get(`/cms/article/${item.id}`, { params: { language: 'pl' } })
          .then((res: any) => {
            const urlField = res.article_fields?.find((f: any) => f.symbol === URL_FIELD_SYMBOL);
            const urlVal = urlField?.value?.find((v: any) => v.language === 'pl')?.translation || '';
            return { id: item.id, url: urlVal };
          })
          .catch(() => ({ id: item.id, url: '' }))
      )
    ).then((results) => {
      const map: Record<number, string> = {};
      results.forEach(({ id, url }) => { map[id] = url; });
      setUrlMap(map);
    });
  }, [data]);

  const handleSaved = () => {
    setEditingId(null);
    refetch();
  };

  // ── Widok edytora ──────────────────────────────────────────────────────────
  if (editingId !== null) {
    return (
      <EditPage
        articleId={editingId === 'new' ? undefined : editingId}
        onBack={() => setEditingId(null)}
        onSaved={handleSaved}
      />
    );
  }

  // ── Widok listy ────────────────────────────────────────────────────────────
  const skeletons = Array.from({ length: 6 }).map((_, i) => (
    <div key={i} className={styles.skeletonCard}>
      <div className={styles.skeletonTop} />
      <div className={styles.skeletonBody}>
        <div className={styles.skeletonLine} />
        <div className={styles.skeletonLine} />
      </div>
    </div>
  ));

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}><Trans>Podstrony</Trans></h1>
          <p className={styles.subtitle}>
            {data?.total_count !== undefined
              ? `${data.total_count} ${t('podstron')}`
              : '…'}
          </p>
        </div>

        <Button onClick={() => setEditingId('new')}>
          <FileEarmarkPlus size={14} style={{ marginRight: 4 }} />
          <Trans>Nowa podstrona</Trans>
        </Button>
      </div>

      <div className={styles.grid}>
        {isLoading ? (
          skeletons
        ) : !data?.items?.length ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>📄</span>
            <span className={styles.emptyText}>
              <Trans>Brak podstron. Utwórz pierwszą podstronę.</Trans>
            </span>
            <Button onClick={() => setEditingId('new')}>
              <Trans>Utwórz podstronę</Trans>
            </Button>
          </div>
        ) : (
          data.items.map((item: ICmsArticleItem) => (
            <PageCard
              key={item.id}
              item={item}
              urlValue={urlMap[item.id] ?? null}
              onEdit={(id) => setEditingId(id)}
              onDeleted={() => refetch()}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default PanelPages;
